import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import "../styles/BlogPost.css";

import { Helmet } from "react-helmet-async";

import { blogPosts } from "../data/blogPosts";

export default function BlogPost() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();

  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <p>{t("blog.error")}</p>;

  const sections = t(post.sectionsKey, { returnObjects: true });

  const faq = t(post.faqKey, { returnObjects: true });

  const relatedPosts = blogPosts
  .filter((p) => p.id !== post.id)
  .map((p) => ({
    ...p,
    score: p.tags.filter((tag) => post.tags.includes(tag)).length
  }))
  .filter((p) => p.score > 0)
  .sort((a, b) => b.score - a.score)
  .slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{`${t(post.titleKey)} | Alfasol`}</title>

        <meta
          name="description"
          content={t(post.descriptionKey)}
        />

        <meta
          name="keywords"
          content={post.keywords.join(", ")}
        />

        <link
          rel="canonical"
          href={`https://alfasol.es/blog/${post.slug}`}
        />

        <meta property="og:type" content="article" />

        <meta
          property="og:title"
          content={t(post.titleKey)}
        />

        <meta
          property="og:description"
          content={t(post.descriptionKey)}
        />

        <meta property="article:author" content="Alfasol" />

        <meta
          property="og:image"
          content={`https://alfasol.es${post.image}`}
        />

        <meta
          property="og:url"
          content={`https://alfasol.es/blog/${post.slug}`}
        />

        <meta
          property="article:published_time"
          content={post.publishedTime}
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",

            headline: t(post.titleKey),
            description: t(post.descriptionKey),

            image: `https://alfasol.es${post.image}`,

            url: `https://alfasol.es/blog/${post.slug}`,

            inLanguage: i18n.language,

            keywords: post.keywords.join(", "),

            datePublished: post.publishedTime,
            dateModified: post.publishedTime,

            wordCount: sections
              .flatMap((s) => s.text.split(/\s+/))
              .length,

            author: {
              "@type": "Organization",
              name: "Alfasol"
            },

            publisher: {
              "@type": "Organization",
              name: "Alfasol",
              logo: {
                "@type": "ImageObject",
                url: "https://alfasol.es/logo.png"
              }
            },

            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://alfasol.es/blog/${post.slug}`
            }
          })}
        </script>
      </Helmet>

      <div className="blogpost-container">
        <div className="blog-breadcrumb">
          <Link to="/">{t("blog.home")}</Link>
          <span>›</span>
          <Link to="/blog">Blog</Link>
          <span>›</span>
          <span>{t(post.titleKey)}</span>
        </div>

        <h1>{t(post.titleKey)}</h1>

        <div className="blogpost-meta">
          <span>
            📅{" "}
            <time dateTime={post.publishedTime}>
              {t(post.dateKey)}
            </time>
          </span>
          <span>👤 {t("blog.author")}</span>
          <span>⏱️ {t(post.readingTimeKey)}</span>
        </div>

        <div className="blog-tags">
          {post.tags.map((tag) => (
            <span key={tag} className="blog-tag">
              {t(tag)}
            </span>
          ))}
        </div>

        <img
          src={post.img}
          alt={t(post.titleKey)}
          className="blogpost-img"
        />

        <div className="blog-index">
          <h3>{t("blog.inThisArticle")}</h3>

          <ul>
            {sections.map((section, index) => (
              <li key={index}>
                <a href={`#section-${index}`}>
                  {section.heading}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="blogpost-content">
          {sections.map((section, index) => (
            <section
              key={index}
              id={`section-${index}`}
            >
              <h2>{section.heading}</h2>

              {section.text
                .split("\n\n")
                .map((paragraph, pIndex) => (
                  <p key={pIndex}>{paragraph}</p>
                ))}
            </section>
          ))}
        </div>

        <div className="blog-faq">
          <h2>{t("blog.faqTitle")}</h2>

          {faq.map((item, index) => (
            <div key={index} className="blog-faq-item">
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </div>
          ))}
        </div>

        {relatedPosts.length > 0 && (
          <div className="related-posts">
            <h2>{t("blog.relatedArticles")}</h2>

            <div className="related-posts-grid">
              {relatedPosts.map((related) => (
                <Link
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  className="related-post-card"
                >
                  <img src={related.img} alt={t(related.titleKey)} />

                  <h3>{t(related.titleKey)}</h3>

                  <p>{t(related.excerptKey)}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        <Link to="/blog" className="back-to-blog">
          ← {t("blog.backToBlog")}
        </Link>
      </div>
    </>
  );
}